import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import site from '../content/site';

// Keeps <head> correct across client-side navigation.
//
// The prerenderer (scripts/prerender.mjs) gives every URL correct metadata on
// first load, but React Router navigations never touch <head> — so without
// this, clicking from a listing to /benefits would leave the previous page's
// title, description and canonical in place. That matters for Google's
// rendered pass and for anything that scrapes a shared link.
//
// Pass `path` for a route defined in site.js ROUTE_META, or pass explicit
// title/description/canonicalPath for dynamic pages like a restaurant listing.

const { ROUTE_META, ORIGIN, DEFAULT_OG_IMAGE } = site;

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function Seo({ path, title, description, canonicalPath, image, index }) {
  const meta = path ? ROUTE_META[path] : null;

  const resolvedTitle = title ?? meta?.title;
  const resolvedDescription = description ?? meta?.description;
  const resolvedPath = canonicalPath ?? path;
  const resolvedIndex = index ?? meta?.index ?? true;
  const resolvedImage = image || DEFAULT_OG_IMAGE;

  useEffect(() => {
    if (resolvedTitle) document.title = resolvedTitle;

    const canonical = resolvedPath ? `${ORIGIN}${resolvedPath}` : null;
    if (canonical) upsertCanonical(canonical);

    upsertMeta('name', 'description', resolvedDescription);
    upsertMeta('name', 'robots', resolvedIndex ? 'index, follow' : 'noindex, follow');

    upsertMeta('property', 'og:title', resolvedTitle);
    upsertMeta('property', 'og:description', resolvedDescription);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', resolvedImage);
    upsertMeta('property', 'twitter:title', resolvedTitle);
    upsertMeta('property', 'twitter:description', resolvedDescription);
    upsertMeta('property', 'twitter:url', canonical);
    upsertMeta('property', 'twitter:image', resolvedImage);
  }, [resolvedTitle, resolvedDescription, resolvedPath, resolvedIndex, resolvedImage]);

  return null;
}

// Mounted once at the app root. Applies the ROUTE_META entry for whatever
// static route is currently active, so individual pages don't each need to
// remember to declare their own metadata.
//
// Parameterised routes are skipped: /business/:id supplies real per-listing
// metadata from the page itself, and everything else with an :id (redemption)
// is a functional screen that gets a plain noindex.
export function RouteSeo() {
  const { pathname } = useLocation();

  if (ROUTE_META[pathname]) {
    return <Seo path={pathname} />;
  }
  if (pathname.startsWith('/business/')) {
    return null; // RestaurantPage renders its own Seo once the listing loads.
  }
  return (
    <Seo
      title={ROUTE_META['/'].title}
      description={ROUTE_META['/'].description}
      canonicalPath={pathname}
      index={false}
    />
  );
}
